/**
 * Admin Review Moderation Dashboard
 * Allows admins/moderators to review flagged reviews and take action
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Rating,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Flag,
  CheckCircle,
  Cancel,
  Delete,
  Visibility,
  Person,
  Star,
  MoreVert
} from '@mui/icons-material';
import { formatDate, formatRelativeTime } from '../../utils/helpers';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DialogCloseButton from '../common/DialogCloseButton';

const ReviewModerationDashboard = () => {
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [moderationDialog, setModerationDialog] = useState(false);
  const [moderationAction, setModerationAction] = useState('');
  const [moderationNotes, setModerationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalFlagged: 0,
    pendingReview: 0,
    resolvedToday: 0
  });

  const { user } = useAuth();

  useEffect(() => {
    loadFlaggedReviews();
  }, [page]);

  const loadFlaggedReviews = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/reviews/flagged', {
        params: { page, limit: 20 }
      });

      if (response.data.success) {
        setFlaggedReviews(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        
        // Update stats
        setStats({
          totalFlagged: response.data.pagination.total,
          pendingReview: response.data.data.length,
          resolvedToday: 0 // Could be calculated from a separate endpoint
        });
      }
    } catch (error) {
      console.error('Error loading flagged reviews:', error);
      setError(error.response?.data?.message || 'Failed to load flagged reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleModerateReview = async () => {
    if (!moderationAction) {
      setError('Please select an action');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await api.post(
        `/reviews/${selectedReview.id}/moderate`,
        {
          action: moderationAction,
          reviewType: selectedReview.review_type,
          moderationNotes
        }
      );

      if (response.data.success) {
        setSuccess(`Review ${moderationAction}ed successfully`);
        setModerationDialog(false);
        setSelectedReview(null);
        setModerationAction('');
        setModerationNotes('');
        loadFlaggedReviews(); // Refresh list
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error moderating review:', error);
      setError(error.response?.data?.message || 'Failed to moderate review');
    } finally {
      setSubmitting(false);
    }
  };

  const openModerationDialog = (review) => {
    setSelectedReview(review);
    setModerationDialog(true);
    setModerationAction('');
    setModerationNotes('');
    setError('');
  };

  const closeModerationDialog = () => {
    setModerationDialog(false);
    setSelectedReview(null);
    setModerationAction('');
    setModerationNotes('');
    setError('');
  };

  const getRoleColor = (role) => {
    const colors = {
      contractor: 'primary',
      verifier: 'success',
      quality_inspector: 'warning',
      client: 'info'
    };
    return colors[role] || 'default';
  };

  const getActionIcon = (action) => {
    const icons = {
      approve: <CheckCircle />,
      reject: <Cancel />,
      remove: <Delete />
    };
    return icons[action] || <MoreVert />;
  };

  const getActionColor = (action) => {
    const colors = {
      approve: 'success',
      reject: 'warning',
      remove: 'error'
    };
    return colors[action] || 'default';
  };

  if (!user || (user.role !== 'admin' && user.role !== 'moderator')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Review Moderation Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and moderate flagged user reviews across all roles
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Badge badgeContent={stats.totalFlagged} color="error">
                  <Flag sx={{ fontSize: 40, color: 'error.main' }} />
                </Badge>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.totalFlagged}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Flagged
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Visibility sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.pendingReview}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Review
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.resolvedToday}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Resolved Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="All Flagged Reviews" />
          <Tab label="High Priority" />
          <Tab label="Recent" />
        </Tabs>
      </Box>

      {/* Reviews List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : flaggedReviews.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No flagged reviews to moderate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All reviews have been processed
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {flaggedReviews.map((review) => (
            <Card key={review.id} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={3}>
                  {/* Review Header */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Avatar>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {review.reviewer_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Reviewing: {review.reviewee_name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={review.reviewee_role.replace('_', ' ')} 
                              size="small" 
                              color={getRoleColor(review.reviewee_role)}
                            />
                            <Chip 
                              label={review.review_type.replace('_', ' ')} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Rating value={review.rating} readOnly size="small" />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatRelativeTime(review.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Review Content */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    {review.review_title && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {review.review_title}
                      </Typography>
                    )}
                    <Typography variant="body1" paragraph>
                      {review.review_text || review.comment}
                    </Typography>
                  </Grid>

                  {/* Flag Information */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'error.lighter', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'error.light'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Flag color="error" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Flagged Reason:
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {review.flagged_reason || 'No reason provided'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Flagged {formatRelativeTime(review.flagged_at)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Actions */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => {
                          setModerationAction('approve');
                          openModerationDialog(review);
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<Cancel />}
                        onClick={() => {
                          setModerationAction('reject');
                          openModerationDialog(review);
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => {
                          setModerationAction('remove');
                          openModerationDialog(review);
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
              <Button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Typography sx={{ lineHeight: '36px' }}>
                Page {page} of {totalPages}
              </Typography>
              <Button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Moderation Dialog */}
      <Dialog
        open={moderationDialog}
        onClose={closeModerationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={closeModerationDialog} />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getActionIcon(moderationAction)}
            <span>
              {moderationAction.charAt(0).toUpperCase() + moderationAction.slice(1)} Review
            </span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {selectedReview && (
            <Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                You are about to {moderationAction} this review. Please add any moderation notes below.
              </Typography>

              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Review Summary
                </Typography>
                <Typography variant="body2">
                  <strong>Reviewer:</strong> {selectedReview.reviewer_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Reviewee:</strong> {selectedReview.reviewee_name} ({selectedReview.reviewee_role})
                </Typography>
                <Typography variant="body2">
                  <strong>Rating:</strong> {selectedReview.rating}/5
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Moderation Notes (Optional)"
                value={moderationNotes}
                onChange={(e) => setModerationNotes(e.target.value)}
                placeholder="Add any notes about this moderation decision..."
                helperText="These notes will be stored for auditing purposes"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModerationDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleModerateReview}
            disabled={submitting}
            variant="contained"
            color={getActionColor(moderationAction)}
            startIcon={submitting ? <CircularProgress size={20} /> : getActionIcon(moderationAction)}
          >
            {submitting ? 'Processing...' : `Confirm ${moderationAction}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewModerationDashboard;

